import { isFailed } from "@/api/helper"
import { alarmRecordCount, alarmRecordRead, popupList } from "@/api/moudules/alarm"
import { IPopupListItem } from "@/api/moudules/alarm.types"
import { computed, getCurrentInstance, inject, onBeforeUnmount, onMounted, provide, Ref, ref, watch } from "vue"
import { useI18n } from "./useI18n"
import { diffDuration } from "@/utils/datetime"
import { ISystemNoticeListItem } from "@/api/moudules/systemNotice.types"
import _ from "lodash"
import { systemNoticeQueryList, systemNoticeRead } from "@/api/moudules/systemNotice"
import dayjs from "dayjs"
import { IEigenvalueInfo } from "@/api/moudules/wave.types"
import { E_Eigenvalue } from "@/types"
import { IMenuItem } from "@/view_helper/mainMenu"
import { buildFullFileUrl } from "@/api/moudules/file"
import { alarmVoiceSetting } from "@/api/moudules/alarmVoice"
import { E_AlarmVoiceBroadcastMode, IAlarmVoiceSetting } from "@/api/moudules/alarmVoice.types"
import { E_YesNoNumber } from "@/api/types"
import APlayer from "aplayer"
import "aplayer/dist/APlayer.min.css"

const ALARM_VOICE_PERMISSION = 'business:alarmVoice:list'
const CANCEL_ALARM_AUDIO_QUEUE = '__cancelAlarmAudioQueue'

interface IAlarmAudioQueueItem {
    queueId: number
    alarmId: number
    audioUrl: string
    name: string
    description: string
    tooltip: string
}

interface IAPlayerAudio {
    name: string
    artist: string
    url: string
    queueId: number
    tooltip: string
}

interface IAlarmAPlayer {
    audio: HTMLAudioElement
    list: {
        index: number
        audios: IAPlayerAudio[]
        add: (audios: IAPlayerAudio[]) => void
        clear: () => void
        remove: (index: number) => void
        show: () => void
    }
    on: (event: string, callback: () => void) => void
    pause: () => void
    destroy: () => void
}

export function useAppNotices() {
    const {
        $t,
    } = useI18n()
    const instance = getCurrentInstance()
    function showNotices(data: ISystemNoticeListItem[]) {
        _.each(data, notice => {
            const {
                id,
                msgContent,
                msgTitle,
                startTime,
                endTime,
            } = notice
            let msgContentWithBr = (msgContent || '').replaceAll('\n', '<br>')
            let diff = diffDuration(endTime, startTime)
            let diffTime = diff.humanize()
            let message = $t('system.noticeMessageTemplate', { startTime, diffTime, msgContent: msgContentWithBr }) as string

            let notify = instance.proxy.$notify({
                customClass: 'system-updata',
                title: msgTitle,
                message,
                dangerouslyUseHTMLString: true,
                duration: 0,
                onClose() {
                    systemNoticeRead({
                        id
                    })
                },
            });
            let ms = dayjs(endTime).diff(dayjs())
            if (ms >= 0) {
                setTimeout(() => {
                    notify.close()
                }, ms)
            } else {
                notify.close()
            }
        })
    }

    function fetchNoticeQuery() {
        systemNoticeQueryList().then(res => {
            if (isFailed(res)) {
                return
            }
            showNotices(res.data)
        })
    }
    return {
        fetchNoticeQuery,
    }
}

export function useAppFechAlarmCountLoop(
    enabled: Ref<boolean>
) {
    const {
        $t,
    } = useI18n()
    const instance = getCurrentInstance()

    const showAlarmPopupList = ref(false)
    let fetchInterval = null
    watch(computed(() => {
        return enabled.value && !showAlarmPopupList.value
    }), (needSchedule: boolean) => {
        clearInterval(fetchInterval)
        fetchInterval = null
        if (needSchedule) {
            fetchInterval = setInterval(() => {
                fetchAlaramCount()
            }, 60 * 1000)
        }
    }, { immediate: true })

    //#region 报警通知
    let lastAlarmTotal: number
    let lastAlarmNotify = ref(null)
    const alarmAudioPlayerRef = ref<HTMLElement>(null)
    const showAlarmAudioPlayer = ref(false)
    let alarmAudioPlayer: IAlarmAPlayer = null
    let alarmAudioQueueId = 0
    let alarmAudioQueue: IAlarmAudioQueueItem[] = []
    let currentAlarmAudioQueueId: number = null
    let isResettingAlarmAudioPlayer = false
    const scheduledAlarmIds = new Set<number>()
    const readAlarmIds = new Set<number>()
    let hasAlarmAudioRetryListener = false

    function removeAlarmAudioRetryListener() {
        if (!hasAlarmAudioRetryListener) return
        document.removeEventListener('pointerdown', retryPendingAlarmAudio, true)
        document.removeEventListener('keydown', retryPendingAlarmAudio, true)
        hasAlarmAudioRetryListener = false
    }

    function clearAlarmAudioRetry() {
        removeAlarmAudioRetryListener()
    }

    function retryPendingAlarmAudio() {
        clearAlarmAudioRetry()
        if (!enabled.value) return
        playCurrentAlarmAudio()
    }

    function waitForUserInteractionToPlay() {
        if (hasAlarmAudioRetryListener) return
        document.addEventListener('pointerdown', retryPendingAlarmAudio, true)
        document.addEventListener('keydown', retryPendingAlarmAudio, true)
        hasAlarmAudioRetryListener = true
    }

    function toAPlayerAudio(item: IAlarmAudioQueueItem): IAPlayerAudio {
        return {
            name: item.name,
            artist: item.description,
            url: buildFullFileUrl(item.audioUrl),
            queueId: item.queueId,
            tooltip: item.tooltip,
        }
    }

    function refreshAlarmAudioTextTips() {
        if (!alarmAudioPlayerRef.value || !alarmAudioPlayer) return
        const listItems = alarmAudioPlayerRef.value.querySelectorAll<HTMLElement>('.aplayer-list li')
        listItems.forEach((element, index) => {
            element.title = alarmAudioPlayer.list.audios[index]?.tooltip || ''
        })
        const currentAudio = alarmAudioPlayer.list.audios[alarmAudioPlayer.list.index]
        const currentMusic = alarmAudioPlayerRef.value.querySelector<HTMLElement>('.aplayer-music')
        if (currentMusic) {
            currentMusic.title = currentAudio?.tooltip || ''
        }
    }

    function makeAlarmAudioQueueItem(
        record: IPopupListItem & { audioUrl: string },
        repeatIndex = 1,
        repeatTotal = 1,
    ): IAlarmAudioQueueItem {
        const equipmentAndPoint = [record.equipmentName, record.mappingName]
            .filter(Boolean)
            .join(' / ')
        const alarmLevel = instance.proxy.$enumTrans.AlarmLevel(record.alarmLevel)
        const alarmType = instance.proxy.$enumTrans.AlarmType(record.alarmType)
        const repeatProgress = repeatTotal > 1
            ? $t('alarmVoice.repeatProgress', { current: repeatIndex, total: repeatTotal }) as string
            : ''
        const description = [alarmLevel, alarmType, repeatProgress].filter(Boolean).join(' · ')
        const detail = [
            record.areaName,
            record.sensorId
                ? `${$t('alarmVoice.sensorId')}: ${record.sensorId}`
                : '',
            description,
            record.createTime,
        ].filter(Boolean).join(' | ')
        return {
            queueId: ++alarmAudioQueueId,
            alarmId: record.id,
            audioUrl: record.audioUrl,
            name: equipmentAndPoint || $t('alarmVoice.audioPlayerItem', { alarmId: record.id }) as string,
            description: description || $t('alarmVoice.autoBroadcast') as string,
            tooltip: detail || $t('alarmVoice.audioPlayerItem', { alarmId: record.id }) as string,
        }
    }

    function getCurrentPlayerQueueId() {
        return alarmAudioPlayer?.list.audios[alarmAudioPlayer.list.index]?.queueId
            ?? alarmAudioPlayer?.list.audios[0]?.queueId
            ?? null
    }

    function removeAlarmAudioQueueItem(queueId: number, markAsRead = false) {
        const queueIndex = alarmAudioQueue.findIndex(item => item.queueId === queueId)
        if (queueIndex < 0) return
        const [item] = alarmAudioQueue.splice(queueIndex, 1)
        const playerIndex = alarmAudioPlayer?.list.audios.findIndex(audio => audio.queueId === queueId) ?? -1
        if (playerIndex >= 0) {
            alarmAudioPlayer.list.remove(playerIndex)
            refreshAlarmAudioTextTips()
        }
        if (markAsRead) {
            markAlarmAsRead(item.alarmId)
        }
        showAlarmAudioPlayer.value = alarmAudioQueue.length > 0
        if (!alarmAudioQueue.length) {
            currentAlarmAudioQueueId = null
            clearAlarmAudioRetry()
        }
    }

    function handleAlarmAudioEnded() {
        if (isResettingAlarmAudioPlayer || !alarmAudioQueue.length) return
        const queueId = currentAlarmAudioQueueId ?? getCurrentPlayerQueueId()
        currentAlarmAudioQueueId = null
        removeAlarmAudioQueueItem(queueId, true)
        if (alarmAudioQueue.length && alarmAudioPlayer?.audio.paused) {
            setTimeout(playCurrentAlarmAudio)
        }
    }

    function handleAlarmAudioError() {
        if (isResettingAlarmAudioPlayer || !alarmAudioQueue.length) return
        const queueId = currentAlarmAudioQueueId ?? getCurrentPlayerQueueId()
        const item = alarmAudioQueue.find(audio => audio.queueId === queueId)
        console.warn('报警语音加载或解码失败', {
            alarmId: item?.alarmId,
            audioUrl: item ? buildFullFileUrl(item.audioUrl) : alarmAudioPlayer?.audio.currentSrc,
            mediaError: alarmAudioPlayer?.audio.error,
        })
        currentAlarmAudioQueueId = null
        removeAlarmAudioQueueItem(queueId)
        if (alarmAudioQueue.length) {
            setTimeout(playCurrentAlarmAudio)
        }
    }

    function ensureAlarmAudioPlayer() {
        if (alarmAudioPlayer || !alarmAudioPlayerRef.value || !alarmAudioQueue.length) return alarmAudioPlayer
        alarmAudioPlayer = new APlayer({
            container: alarmAudioPlayerRef.value,
            audio: alarmAudioQueue.map(toAPlayerAudio),
            autoplay: false,
            listFolded: false,
            listMaxHeight: '198px',
            loop: 'none',
            order: 'list',
            mutex: false,
            preload: 'auto',
            volume: 1,
            theme: '#0099ff',
        }) as IAlarmAPlayer
        alarmAudioPlayer.on('play', () => {
            currentAlarmAudioQueueId = getCurrentPlayerQueueId()
            clearAlarmAudioRetry()
            refreshAlarmAudioTextTips()
        })
        alarmAudioPlayer.on('listswitch', () => setTimeout(refreshAlarmAudioTextTips))
        alarmAudioPlayer.on('ended', handleAlarmAudioEnded)
        alarmAudioPlayer.on('error', handleAlarmAudioError)
        alarmAudioPlayer.list.show()
        refreshAlarmAudioTextTips()
        return alarmAudioPlayer
    }

    function playCurrentAlarmAudio() {
        if (!enabled.value || !alarmAudioQueue.length) return
        const player = ensureAlarmAudioPlayer()
        if (!player) return
        currentAlarmAudioQueueId = getCurrentPlayerQueueId()
        player.audio.muted = false
        player.audio.volume = 1
        const playPromise = player.audio.play()
        playPromise?.catch(error => {
            const isAutoplayBlocked = error?.name === 'NotAllowedError'
            console.warn(isAutoplayBlocked
                ? '浏览器阻止了报警语音自动播放，将在下一次用户操作时重试'
                : '报警语音播放失败', {
                audioUrl: player.audio.currentSrc || player.audio.src,
                error,
            })
            if (isAutoplayBlocked) {
                waitForUserInteractionToPlay()
                return
            }
            const failedQueueId = currentAlarmAudioQueueId
            currentAlarmAudioQueueId = null
            removeAlarmAudioQueueItem(failedQueueId)
            if (alarmAudioQueue.length) {
                setTimeout(playCurrentAlarmAudio)
            }
        })
    }

    function stopAlarmAudio(clearScheduled = true) {
        alarmAudioQueue = []
        currentAlarmAudioQueueId = null
        showAlarmAudioPlayer.value = false
        if (clearScheduled) {
            scheduledAlarmIds.clear()
        }
        clearAlarmAudioRetry()
        if (alarmAudioPlayer) {
            isResettingAlarmAudioPlayer = true
            alarmAudioPlayer.pause()
            alarmAudioPlayer.list.clear()
            isResettingAlarmAudioPlayer = false
        }
    }

    function cancelAlarmAudioQueue() {
        stopAlarmAudio(false)
    }

    function markAlarmAsRead(alarmId: number) {
        if (readAlarmIds.has(alarmId)) return
        readAlarmIds.add(alarmId)
        alarmRecordRead({ ids: [alarmId] }).then(res => {
            if (isFailed(res)) {
                readAlarmIds.delete(alarmId)
                console.warn('[报警语音] 标记报警已读失败', { alarmId, response: res })
            }
        }).catch(error => {
            readAlarmIds.delete(alarmId)
            console.warn('[报警语音] 标记报警已读失败', { alarmId, error })
        })
    }

    function makeAlarmAudioPlayList(records: IPopupListItem[], setting: IAlarmVoiceSetting) {
        const playableRecords = records.filter((record): record is IPopupListItem & { audioUrl: string } => (
            Boolean(record.id) && Boolean(record.audioUrl) && !scheduledAlarmIds.has(record.id)
        ))
        if (setting.broadcastMode !== E_AlarmVoiceBroadcastMode.RepeatUntilHandled) {
            return playableRecords.slice(0, 1).map(record => makeAlarmAudioQueueItem(record))
        }
        const repeatCount = Math.max(1, Math.floor(Number(setting.repeatCount) || 1))
        return playableRecords.slice(0, 3).flatMap(record => (
            Array.from({ length: repeatCount }, (_, index) => (
                makeAlarmAudioQueueItem(record, index + 1, repeatCount)
            ))
        ))
    }

    function logAlarmAudioPlayList(playList: IAlarmAudioQueueItem[], setting: IAlarmVoiceSetting, recordCount: number) {
        const isRepeat = setting.broadcastMode === E_AlarmVoiceBroadcastMode.RepeatUntilHandled
        const repeatCount = isRepeat
            ? Math.max(1, Math.floor(Number(setting.repeatCount) || 1))
            : 1
        if (!playList.length) {
            console.warn('[报警语音] 没有找到需要播放的语音', {
                broadcastMode: isRepeat ? '未处理时重复播报' : '仅播报一次',
                repeatCount,
                recordCount,
            })
            return
        }
        console.groupCollapsed(`[报警语音] 追加 ${playList.length} 次播放到队列`)
        console.log('播报配置', {
            broadcastMode: isRepeat ? '未处理时重复播报' : '仅播报一次',
            repeatCount,
        })
        console.table(playList.map((item, index) => ({
            playOrder: index + 1,
            alarmId: item.alarmId,
            name: item.name,
            description: item.description,
            audioUrl: buildFullFileUrl(item.audioUrl),
        })))
        console.groupEnd()
    }

    function enqueueAlarmAudio(playList: IAlarmAudioQueueItem[]) {
        if (!playList.length) return
        const wasEmpty = alarmAudioQueue.length === 0
        playList.forEach(item => scheduledAlarmIds.add(item.alarmId))
        alarmAudioQueue.push(...playList)
        showAlarmAudioPlayer.value = true
        if (alarmAudioPlayer) {
            alarmAudioPlayer.list.add(playList.map(toAPlayerAudio))
            alarmAudioPlayer.list.show()
            refreshAlarmAudioTextTips()
        } else {
            ensureAlarmAudioPlayer()
        }
        if (wasEmpty) {
            playCurrentAlarmAudio()
        }
    }

    function closeAlarmNotify(stopAudio = true) {
        if (stopAudio) {
            stopAlarmAudio()
        }
        if (lastAlarmNotify.value) {
            lastAlarmNotify.value.close()
        }
    }

    function fetchAlaramCount() {
        const canPlayAlarmVoice = instance.proxy.$hasPerm(ALARM_VOICE_PERMISSION)
        if (!canPlayAlarmVoice) {
            stopAlarmAudio()
        }
        const popupListTask = canPlayAlarmVoice
            ? Promise.all([
                popupList({
                    pageNum: 1,
                    pageSize: 10,
                }),
                alarmVoiceSetting(),
            ]).catch(() => null)
            : Promise.resolve(null)

        alarmRecordCount().then(res => {
            if (isFailed(res)) {
                return
            }
            let total = res.data.alarmUnreadCount
            if (total > 0) {
                if (lastAlarmTotal !== total || !lastAlarmNotify.value) {
                    if (lastAlarmTotal !== total) {
                        closeAlarmNotify(false)
                    }
                    const alarmNotify = instance.proxy.$notify({
                        title: $t('alarm.alarmTitle') as string,
                        message: $t('alarm.alarmTipTemplate', [total]) as string,
                        offset: 100,
                        duration: 0,
                        customClass: 'alarm-notify',
                        onClick: () => {
                            showAlarmPopupList.value = true
                            closeAlarmNotify()
                        },
                        onClose: () => {
                            if (lastAlarmNotify.value === alarmNotify) {
                                lastAlarmNotify.value = null
                            }
                        },
                    })
                    lastAlarmNotify.value = alarmNotify
                }
                lastAlarmTotal = total

                popupListTask.then(result => {
                    if (!instance.proxy.$hasPerm(ALARM_VOICE_PERMISSION)
                        || !lastAlarmNotify.value
                        || showAlarmPopupList.value
                        || !result) return
                    const [popupRes, settingRes] = result
                    if (isFailed(popupRes)
                        || isFailed(settingRes)
                        || settingRes.data.enabledFlag !== E_YesNoNumber.Yes) return
                    const playList = makeAlarmAudioPlayList(popupRes.data.records, settingRes.data)
                    logAlarmAudioPlayList(playList, settingRes.data, popupRes.data.records.length)
                    enqueueAlarmAudio(playList)
                })
            }
        })
    }
    function onAlarmPopupClose() {
        showAlarmPopupList.value = false        
    }

    watch(enabled, (isEnabled: boolean) => {
        if (!isEnabled) {
            stopAlarmAudio()
        }
    }, { immediate: true })

    onMounted(() => {
        if (alarmAudioQueue.length) {
            ensureAlarmAudioPlayer()
            playCurrentAlarmAudio()
        }
    })

    onBeforeUnmount(() => {
        clearInterval(fetchInterval)
        stopAlarmAudio()
        alarmAudioPlayer?.destroy()
        alarmAudioPlayer = null
    })
    provide(CANCEL_ALARM_AUDIO_QUEUE, cancelAlarmAudioQueue)
    return {
        fetchAlaramCount,
        showAlarmPopupList,
        onAlarmPopupClose,
        closeAlarmNotify,
        alarmAudioPlayerRef,
        showAlarmAudioPlayer,
    }
}

export function injectCancelAlarmAudioQueue() {
    return inject(CANCEL_ALARM_AUDIO_QUEUE, () => {}) as () => void
}

export function useAppEigenvalProvider() {
    const showEigenvalue = ref(false)
    function onCloseEigenvalue() {
        showEigenvalue.value = false
    }
    let eigenvalue = ref({} as IEigenvalueInfo)
    let eigenvalueType = ref(E_Eigenvalue.Acceleration)

    provide('showEigenvalue', (visible: boolean) => {
        showEigenvalue.value = visible
    })
    provide('updateEigenvalue', (value: IEigenvalueInfo, type: E_Eigenvalue) => {
        eigenvalue.value = value
        eigenvalueType.value = type
    })
    return {
        showEigenvalue,
        onCloseEigenvalue,
        eigenvalue,
        eigenvalueType,
    }
}


const __appMenuItemClick = '__appMenuItemClick'

type F_OnClickMenuItem = (item: IMenuItem) => void

export function injectAppMenuItemClick() {
    return inject(__appMenuItemClick) as F_OnClickMenuItem
}

export function provideAppMenuClickHandler(fn: F_OnClickMenuItem) {
    provide(__appMenuItemClick, fn)
}
